/**
 * Sysadmin Models — Wrapper Function Library (Wave 3 / M6c criterion 3).
 *
 * Library view of every sysadmin-authored wrapper function. Each row
 * is the registered Filter (Argus invariant I-M6.7: tenants never
 * see this surface). Layout choices follow the OpenWebUI Functions
 * admin pattern but adapted for our tighter taxonomy:
 *
 *   - Card grid (vs. table) — wrapper functions are conceptually
 *     small + standalone; cards put metadata + actions next to
 *     each other without horizontal scrolling.
 *   - Filter bar at the top: text search + runtime + category +
 *     applies-to + active-only toggle.
 *   - Click a card → detail dialog with Monaco source viewer
 *     (read-only per anchor doc; editing lands when Forge cell
 *     ships in Wave 6+), a Used-by section showing which brands
 *     reference this function in their chain, and a Trust-toggle
 *     count badge.
 *   - Per-card actions: View source / Show usage / Activate or
 *     Retire (the active flag is the safe rollback in v1; full
 *     edit/version-control is post-M6c).
 *
 * Per I-M6.7 + I-M6.4 this surface is sysadmin-only. The
 * /sysadmin parent layout enforces requireSysadmin.
 *
 * See:
 *   - docs/waves/wave-3/M6C-MODELS-UI-ANCHOR.md (anchor)
 *   - docs/decisions/0020-*.md (architecture lock)
 *   - packages/api/src/routes/v1/admin/argus-models.ts
 *     (the M6c admin endpoints this page consumes)
 */

import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Library,
  Search,
  Code2,
  Eye,
  Power,
  PowerOff,
  Layers,
  Activity,
  ToggleRight,
  X,
  Cpu,
  ShieldCheck,
  ScanText,
  PenSquare,
  Hash,
  Calendar,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import {
  useWrapperFunctions,
  useWrapperFunctionUsage,
  useUpdateWrapperFunction,
  useCreateWrapperFunction,
  useUpdateWrapperFunctionSource,
  useSignWrapperFunction,
  useDeleteWrapperFunction,
  type WrapperFunction,
} from '@/lib/use-argus-models';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Signature, FileWarning } from 'lucide-react';
import { ModelsSubNav } from './models/index';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/models/functions')({
  component: FunctionsLibraryPage,
});

const RUNTIME_OPTIONS = ['all', 'native', 'ts', 'py'] as const;
const APPLIES_TO_OPTIONS = ['all', 'inlet', 'outlet', 'both'] as const;

function FunctionsLibraryPage() {
  const [searchInput, setSearchInput] = useState('');
  const [runtimeFilter, setRuntimeFilter] = useState<(typeof RUNTIME_OPTIONS)[number]>('all');
  const [appliesFilter, setAppliesFilter] = useState<(typeof APPLIES_TO_OPTIONS)[number]>('all');
  /**
   * R5 P1-4: "Active only" filter defaults to ON.
   *
   * Pre-fix, the default was OFF so the topmost visible card in the
   * functions library was a retired Python `test` function rather than
   * the 9 working wrappers (audit_emit / citation_assembly /
   * citation_required / classification / faithfulness_check /
   * pii_detect / prompt_injection_defense / strict_refusal). Confusing
   * operator UX — the discoverable surface should be the things that
   * actually run.
   *
   * The default-ON is persisted to localStorage under
   * `argus.sysadmin.functions.activeOnly` so a sysadmin who flipped it
   * off intentionally (to audit retired sources) doesn't have to re-toggle
   * every nav. The hook reads from localStorage during initial state
   * resolution and writes on each change.
   */
  const [activeOnly, setActiveOnly] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      const raw = window.localStorage.getItem('argus.sysadmin.functions.activeOnly');
      // Only honour an explicit 'false' override; 'true', null, or any
      // other value resolves to the new default (true).
      return raw !== 'false';
    } catch {
      return true;
    }
  });
  // Persist activeOnly changes so the toggle survives navigation. The
  // effect runs only on the client (typeof window check) and only when
  // the value actually changes (React's effect dependency tracking).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        'argus.sysadmin.functions.activeOnly',
        String(activeOnly),
      );
    } catch {
      // Storage quota / private mode — ignore; in-memory state still works.
    }
  }, [activeOnly]);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [selectedFn, setSelectedFn] = useState<WrapperFunction | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const fnsQuery = useWrapperFunctions();
  const updateMutation = useUpdateWrapperFunction();

  // Distinct categories observed in the library — drive a chip filter.
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const f of fnsQuery.data?.items ?? []) set.add(f.category);
    return Array.from(set).sort();
  }, [fnsQuery.data]);

  const filtered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    return (fnsQuery.data?.items ?? []).filter((f) => {
      if (activeOnly && !f.isActive) return false;
      if (runtimeFilter !== 'all' && f.runtime !== runtimeFilter) return false;
      if (appliesFilter !== 'all' && f.appliesTo !== appliesFilter) return false;
      if (categoryFilter && f.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        f.functionKey.toLowerCase().includes(q) ||
        f.displayName.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q)
      );
    });
  }, [fnsQuery.data, searchInput, runtimeFilter, appliesFilter, activeOnly, categoryFilter]);

  // Group by category for visual separation in the grid.
  const grouped = useMemo(() => {
    const map = new Map<string, WrapperFunction[]>();
    for (const f of filtered) {
      const arr = map.get(f.category) ?? [];
      arr.push(f);
      map.set(f.category, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Library className="h-6 w-6" />
            Wrapper Function Library
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Sysadmin-authored TS / Python / native filters that compose into
            the Argus wrapper chain. Tenants never see this surface — they
            see only the Trust toggles (separate tab) that the engineering
            team chooses to expose. New functions ship as drafts; sign + activate
            once you've reviewed the source.
          </p>
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setCreateOpen(true)}>
          <Plus className="h-3.5 w-3.5" /> New Function
        </Button>
      </div>

      <ModelsSubNav active="/sysadmin/models/functions" />

      {/* Filter bar */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by function key, display name, or description…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <FilterPills
              label="Runtime"
              options={RUNTIME_OPTIONS}
              active={runtimeFilter}
              onPick={(v) => setRuntimeFilter(v)}
            />
            <FilterPills
              label="Phase"
              options={APPLIES_TO_OPTIONS}
              active={appliesFilter}
              onPick={(v) => setAppliesFilter(v)}
            />
            {/*
              * R5 P1-4 — label inverted to "Show retired" so the
              * default (activeOnly=true → switch OFF) reads as the
              * natural "no retired rows by default" state. Sysadmin
              * who explicitly wants to see retired sources flips the
              * switch ON; localStorage persists the choice.
              */}
            <label
              className="flex items-center gap-2 text-sm shrink-0 cursor-pointer"
              title="Show retired wrapper functions alongside the active ones. Off by default so the library shows only the 9 functions actually running in chains."
            >
              <Switch
                checked={!activeOnly}
                onCheckedChange={(showRetired) => setActiveOnly(!showRetired)}
              />
              <span>Show retired</span>
            </label>
          </div>
          {categories.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-muted-foreground mr-1">Category:</span>
              <CategoryPill
                label="All"
                active={categoryFilter === null}
                onClick={() => setCategoryFilter(null)}
              />
              {categories.map((c) => (
                <CategoryPill
                  key={c}
                  label={c}
                  active={categoryFilter === c}
                  onClick={() => setCategoryFilter(c)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card grid */}
      {fnsQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            Failed to load library: {fnsQuery.error.message}
          </CardContent>
        </Card>
      ) : fnsQuery.isLoading ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground animate-pulse">
            Loading function library…
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-2">
            <Library className="h-10 w-10 text-muted-foreground mx-auto" />
            <h3 className="text-base font-semibold">No functions match the current filters</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {searchInput || categoryFilter || runtimeFilter !== 'all' || appliesFilter !== 'all' || activeOnly
                ? 'Adjust the filters above to widen the search.'
                : 'No wrapper functions have been seeded yet. Run the catalog seed to populate the library.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([category, functions]) => (
            <section key={category}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {category} <span className="text-muted-foreground/60">({functions.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {functions.map((fn) => (
                  <FunctionCard
                    key={fn.id}
                    fn={fn}
                    onView={() => setSelectedFn(fn)}
                    onToggleActive={async (next) => {
                      await updateMutation.mutateAsync({ id: fn.id, isActive: next });
                    }}
                    isUpdating={updateMutation.isPending && updateMutation.variables?.id === fn.id}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Footer summary */}
      {!fnsQuery.isLoading && fnsQuery.data && (
        <div className="text-sm text-muted-foreground flex items-center justify-between">
          <span>
            {filtered.length} of {fnsQuery.data.items.length} functions match
          </span>
          <span>
            {fnsQuery.data.items.filter((f) => f.isActive).length} active /{' '}
            {fnsQuery.data.items.length} total
          </span>
        </div>
      )}

      <FunctionDetailDialog
        fn={selectedFn}
        onClose={() => setSelectedFn(null)}
        onChanged={(next) => setSelectedFn(next)}
      />

      <NewFunctionSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────

function FunctionCard({
  fn,
  onView,
  onToggleActive,
  isUpdating,
}: {
  fn: WrapperFunction;
  onView: () => void;
  onToggleActive: (next: boolean) => Promise<void> | void;
  isUpdating: boolean;
}) {
  const [confirmingRetire, setConfirmingRetire] = useState(false);

  return (
    <>
      <Card className={cn(!fn.isActive && 'opacity-70')}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <button
              type="button"
              onClick={onView}
              className="text-left flex-1 min-w-0 group"
            >
              <CardTitle className="text-sm flex items-center gap-2 group-hover:underline">
                <CategoryIcon category={fn.category} className="h-4 w-4 text-muted-foreground" />
                {fn.displayName}
              </CardTitle>
              <div className="text-xs text-muted-foreground font-mono mt-1 truncate">
                {fn.functionKey}
              </div>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              {fn.isActive ? (
                <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">
                  <Power className="h-3 w-3 mr-0.5" />
                  active
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground text-[10px]">
                  retired
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <p className="text-xs text-muted-foreground line-clamp-3">{fn.description}</p>

          <div className="flex items-center gap-1.5 flex-wrap">
            <RuntimeBadge runtime={fn.runtime} />
            <AppliesToBadge appliesTo={fn.appliesTo} />
            <Badge variant="secondary" className="text-[10px]">
              v{fn.version}
            </Badge>
            <Badge variant="outline" className="text-[10px]" title="Latency budget">
              {fn.latencyBudgetMs}ms
            </Badge>
          </div>

          <div className="flex items-center gap-1 pt-1">
            <Button size="sm" variant="ghost" onClick={onView} className="text-xs">
              <Eye className="h-3.5 w-3.5 mr-1" />
              View
            </Button>
            {fn.isActive ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={isUpdating}
                onClick={() => setConfirmingRetire(true)}
                className="text-xs text-amber-700 hover:text-amber-800"
              >
                {isUpdating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PowerOff className="h-3.5 w-3.5 mr-1" />}
                Retire
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={isUpdating}
                onClick={() => onToggleActive(true)}
                className="text-xs text-emerald-700 hover:text-emerald-800"
              >
                {isUpdating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Power className="h-3.5 w-3.5 mr-1" />}
                Activate
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <RetireConfirmDialog
        open={confirmingRetire}
        onOpenChange={setConfirmingRetire}
        functionName={fn.displayName}
        functionId={fn.id}
        onConfirm={async () => {
          await onToggleActive(false);
          setConfirmingRetire(false);
        }}
      />
    </>
  );
}

// ─── Detail dialog ────────────────────────────────────────────────────────

function FunctionDetailDialog({
  fn,
  onClose,
  onChanged,
}: {
  fn: WrapperFunction | null;
  onClose: () => void;
  onChanged: (next: WrapperFunction) => void;
}) {
  const usageQuery = useWrapperFunctionUsage(fn?.id ?? null);
  const updateMutation = useUpdateWrapperFunction();
  const updateSource = useUpdateWrapperFunctionSource();
  const signFn = useSignWrapperFunction();
  const deleteFn = useDeleteWrapperFunction();

  const [activeTab, setActiveTab] = useState<'metadata' | 'source' | 'usage'>('metadata');
  const [draftSource, setDraftSource] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Reset draftSource whenever the displayed function changes.
  useMemo(() => {
    setDraftSource(fn?.source ?? '');
    return null;
  }, [fn?.id, fn?.source]);

  if (!fn) return null;

  const isNative = fn.runtime === 'native';
  const sourceDirty = !isNative && draftSource !== fn.source;
  // Native rows are signed by binary identity; not by signature column.
  const isSigned = isNative ? true : !!fn.signingKeyId;

  const handleSaveSource = async () => {
    if (!fn || !sourceDirty) return;
    try {
      const result = await updateSource.mutateAsync({ id: fn.id, source: draftSource });
      toast.success('Source saved (signature invalidated — re-sign to activate)');
      onChanged({ ...fn, source: draftSource, isActive: result.isActive, signature: null, signingKeyId: null } as WrapperFunction);
    } catch (err) {
      toast.error('Save failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    }
  };

  const handleSign = async () => {
    if (!fn) return;
    try {
      const result = await signFn.mutateAsync({ id: fn.id });
      toast.success(`Signed with key ${result.signingKeyId}`);
      // Re-fetch happens via list invalidation; mirror locally so the
      // dialog reflects the new signature state immediately.
      onChanged({ ...fn, signingKeyId: result.signingKeyId } as WrapperFunction);
    } catch (err) {
      toast.error('Signing failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    }
  };

  const handleToggleActive = async (next: boolean) => {
    if (!fn) return;
    try {
      await updateMutation.mutateAsync({ id: fn.id, isActive: next });
      onChanged({ ...fn, isActive: next });
      toast.success(next ? 'Function activated' : 'Function retired');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('UNSIGNED_FUNCTION')) {
        toast.error('Cannot activate an unsigned function', {
          description: 'Save + sign the source first.',
        });
      } else {
        toast.error('Toggle failed', { description: msg });
      }
    }
  };

  const handleDelete = async () => {
    if (!fn) return;
    try {
      await deleteFn.mutateAsync({ id: fn.id });
      toast.success('Function deleted');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('FUNCTION_IN_USE')) {
        toast.error('Cannot delete', { description: 'Still wired into a brand chain. Remove from chains first.' });
      } else if (msg.includes('CANNOT_DELETE_NATIVE')) {
        toast.error('Cannot delete a native function', { description: 'Native rows are seeded; deactivate instead.' });
      } else {
        toast.error('Delete failed', { description: msg });
      }
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open={!!fn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CategoryIcon category={fn.category} className="h-5 w-5 text-muted-foreground" />
            {fn.displayName}
            {!fn.isActive && (
              <Badge variant="outline" className="text-muted-foreground text-[10px] ml-2">
                retired
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{fn.functionKey}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as typeof activeTab)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="metadata">
              <ScanText className="h-3.5 w-3.5 mr-1.5" />
              Metadata
            </TabsTrigger>
            <TabsTrigger value="source">
              <Code2 className="h-3.5 w-3.5 mr-1.5" />
              Source
            </TabsTrigger>
            <TabsTrigger value="usage">
              <Activity className="h-3.5 w-3.5 mr-1.5" />
              Usage{' '}
              {usageQuery.data && (
                <span className="ml-1 text-muted-foreground">({usageQuery.data.items.length})</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Metadata */}
          <TabsContent value="metadata" className="flex-1 overflow-auto px-1 py-3">
            <div className="space-y-4 text-sm">
              <Field label="Description" value={fn.description} />
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Runtime"
                  value={<RuntimeBadge runtime={fn.runtime} />}
                />
                <Field
                  label="Applies to"
                  value={<AppliesToBadge appliesTo={fn.appliesTo} />}
                />
                <Field label="Category" value={fn.category} mono />
                <Field
                  label="Latency budget"
                  value={
                    <span className="font-mono">
                      {fn.latencyBudgetMs}
                      <span className="text-muted-foreground">ms</span>
                    </span>
                  }
                />
                <Field
                  label="Version"
                  value={<span className="font-mono">v{fn.version}</span>}
                />
                <Field
                  label="Status"
                  value={
                    fn.isActive ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                        active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        retired
                      </Badge>
                    )
                  }
                />
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4 text-xs">
                <Field
                  label="Created"
                  value={
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {fn.createdAt.split('T')[0]}
                    </span>
                  }
                />
                <Field
                  label="Updated"
                  value={
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {fn.updatedAt.split('T')[0]}
                    </span>
                  }
                />
              </div>
              <Separator />
              <Field
                label="ID"
                value={<code className="font-mono text-[11px] text-muted-foreground">{fn.id}</code>}
              />
            </div>
          </TabsContent>

          {/* Source */}
          <TabsContent value="source" className="flex-1 overflow-hidden px-0 py-2 space-y-2">
            <div className="border rounded-md overflow-hidden h-[400px]">
              <Editor
                height="100%"
                language={runtimeToMonacoLang(fn.runtime)}
                value={isNative ? fn.source : draftSource}
                onChange={(v) => !isNative && setDraftSource(v ?? '')}
                options={{
                  readOnly: isNative,
                  minimap: { enabled: false },
                  fontSize: 12,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                }}
                theme="vs-dark"
              />
            </div>
            {isNative ? (
              <p className="text-xs text-muted-foreground px-1 flex items-start gap-1.5">
                <FileWarning className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                Native functions are bound to compiled-in modules. The source
                field stores a pointer string, not editable bytes — rewire
                via a b-series migration if a native function needs to change.
              </p>
            ) : (
              <div className="flex items-center justify-between gap-3 px-1">
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  {sourceDirty ? (
                    <span className="text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Unsaved edits
                    </span>
                  ) : isSigned ? (
                    <span className="text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                      <Signature className="h-3 w-3" />
                      Signed by {fn.signingKeyId}
                    </span>
                  ) : (
                    <span className="text-muted-foreground inline-flex items-center gap-1">
                      <FileWarning className="h-3 w-3" />
                      Unsigned — must sign before activation
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!sourceDirty || updateSource.isPending}
                    onClick={handleSaveSource}
                    className="gap-1.5"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {updateSource.isPending ? 'Saving…' : 'Save source'}
                  </Button>
                  <Button
                    size="sm"
                    disabled={sourceDirty || isSigned || signFn.isPending}
                    onClick={handleSign}
                    className="gap-1.5"
                    title={
                      sourceDirty
                        ? 'Save the source first'
                        : isSigned
                          ? 'Already signed'
                          : 'Sign the current source with the platform Ed25519 key'
                    }
                  >
                    <Signature className="h-3.5 w-3.5" />
                    {signFn.isPending ? 'Signing…' : 'Sign'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Usage */}
          <TabsContent value="usage" className="flex-1 overflow-auto px-1 py-3">
            <UsagePanel
              loading={usageQuery.isLoading}
              error={usageQuery.error}
              items={usageQuery.data?.items ?? []}
              trustToggleCount={usageQuery.data?.trustToggleCount ?? 0}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4 gap-2 flex-wrap">
          {!isNative && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:bg-destructive/10 mr-auto"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {fn.isActive ? (
            <Button
              variant="destructive"
              onClick={() => handleToggleActive(false)}
            >
              <PowerOff className="h-4 w-4 mr-1" />
              Retire function
            </Button>
          ) : (
            <Button
              onClick={() => handleToggleActive(true)}
              disabled={!isSigned}
              title={
                isSigned
                  ? 'Activate this function — runs in every brand chain it is wired into'
                  : 'Sign the source first (Source tab)'
              }
            >
              <Power className="h-4 w-4 mr-1" />
              Activate function
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete wrapper function</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <code className="bg-muted px-1 rounded">{fn.functionKey}</code>?
              This refuses if the function is still wired into any brand's
              chain — remove it from the brand detail page first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete function
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// ─── Usage panel ──────────────────────────────────────────────────────────

function UsagePanel({
  loading,
  error,
  items,
  trustToggleCount,
}: {
  loading: boolean;
  error: Error | null;
  items: ReturnType<typeof useWrapperFunctionUsage>['data'] extends infer D
    ? D extends { items: infer I }
      ? I
      : never
    : never;
  trustToggleCount: number;
}) {
  if (loading) {
    return <div className="text-sm text-muted-foreground animate-pulse">Loading usage…</div>;
  }
  if (error) {
    return <div className="text-sm text-destructive">Failed to load usage: {error.message}</div>;
  }

  const arr = items as Array<{
    argusModelId: string;
    brandId: string;
    displayName: string;
    modality: string;
    chainKind: 'inlet' | 'outlet';
    position: number;
    isMandatory: boolean;
    failurePolicy: 'terminal' | 'warn';
    isActive: boolean;
  }>;

  if (arr.length === 0 && trustToggleCount === 0) {
    return (
      <div className="text-center py-8 space-y-2">
        <Layers className="h-8 w-8 text-muted-foreground mx-auto" />
        <p className="text-sm font-medium">Not in use</p>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto">
          No Argus model references this function in its chain, and no Trust
          toggle wires to it. It can be retired without breaking any tenant.
        </p>
      </div>
    );
  }

  // Group by chainKind for visual scanning.
  const inlets = arr.filter((u) => u.chainKind === 'inlet');
  const outlets = arr.filter((u) => u.chainKind === 'outlet');

  return (
    <div className="space-y-4">
      {trustToggleCount > 0 && (
        <Card className="bg-amber-50/50 border-amber-200">
          <CardContent className="py-3 flex items-start gap-3 text-sm">
            <ToggleRight className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-amber-900">
                Wired to {trustToggleCount} Trust {trustToggleCount === 1 ? 'toggle' : 'toggles'}
              </div>
              <div className="text-xs text-amber-700 mt-0.5">
                Retiring this function will leave the toggle{trustToggleCount === 1 ? '' : 's'} pointing
                at an inactive function. Tenants who flip the toggle on will see no effect. Edit
                affected toggles on the Trust Toggles page first.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {inlets.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Inlet uses ({inlets.length})
          </h3>
          <UsageList items={inlets} />
        </div>
      )}

      {outlets.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Outlet uses ({outlets.length})
          </h3>
          <UsageList items={outlets} />
        </div>
      )}

      {arr.some((u) => u.isMandatory) && (
        <Card className="bg-red-50/50 border-red-200">
          <CardContent className="py-3 flex items-start gap-3 text-sm">
            <AlertTriangle className="h-4 w-4 text-red-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-red-900">Mandatory in one or more chains</div>
              <div className="text-xs text-red-700 mt-0.5">
                Retiring this function will silently strip a mandatory step from those brands'
                wrapper chains. Audit-emit, classification, and faithfulness checks must stay
                running for compliance — confirm impact before proceeding.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function UsageList({
  items,
}: {
  items: Array<{
    argusModelId: string;
    brandId: string;
    displayName: string;
    modality: string;
    chainKind: 'inlet' | 'outlet';
    position: number;
    isMandatory: boolean;
    failurePolicy: 'terminal' | 'warn';
    isActive: boolean;
  }>;
}) {
  return (
    <div className="space-y-1">
      {items.map((u, idx) => (
        <div
          key={`${u.argusModelId}-${u.chainKind}-${u.position}-${idx}`}
          className={cn(
            'flex items-center gap-3 border rounded-md p-2 text-sm',
            !u.isActive && 'opacity-60',
          )}
        >
          <span className="font-mono text-xs text-muted-foreground tabular-nums w-12 shrink-0">
            #{u.position}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{u.displayName}</div>
            <div className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
              {u.brandId}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Badge variant="outline" className="text-[10px]">
              {u.modality}
            </Badge>
            {u.isMandatory ? (
              <Badge variant="default" className="text-[10px]">
                mandatory
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-[10px]">
                toggleable
              </Badge>
            )}
            <Badge
              variant="outline"
              className={cn(
                'text-[10px]',
                u.failurePolicy === 'terminal'
                  ? 'border-red-300 text-red-700'
                  : 'border-amber-300 text-amber-700',
              )}
            >
              {u.failurePolicy}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Retire confirmation ──────────────────────────────────────────────────

function RetireConfirmDialog({
  open,
  onOpenChange,
  functionName,
  functionId,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  functionName: string;
  functionId: string;
  onConfirm: () => Promise<void>;
}) {
  const usageQuery = useWrapperFunctionUsage(open ? functionId : null);
  const [busy, setBusy] = useState(false);
  const usageCount = usageQuery.data?.items.length ?? 0;
  const trustCount = usageQuery.data?.trustToggleCount ?? 0;
  const hasMandatory = (usageQuery.data?.items ?? []).some((u) => u.isMandatory);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <PowerOff className="h-5 w-5 text-amber-700" />
            Retire {functionName}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Retiring sets <code>is_active = false</code>. The function row stays in
                the catalog so existing chain references remain valid (the wrapper-resolver
                skips inactive entries at request time). You can re-activate later.
              </p>
              {usageQuery.isLoading ? (
                <p className="text-muted-foreground text-xs">Checking usage…</p>
              ) : (
                <Card className={hasMandatory ? 'border-red-300' : usageCount > 0 ? 'border-amber-300' : ''}>
                  <CardContent className="py-3 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span>Brands using this function</span>
                      <span className="font-mono">{usageCount}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Trust toggles wired</span>
                      <span className="font-mono">{trustCount}</span>
                    </div>
                    {hasMandatory && (
                      <div className="flex items-center gap-1.5 text-red-700 pt-1">
                        <AlertTriangle className="h-3 w-3" />
                        <span>One or more uses are mandatory — please verify compliance impact</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
            className={cn(
              hasMandatory
                ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                : 'bg-amber-600 hover:bg-amber-700 text-white',
            )}
          >
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Retire function
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function FilterPills<T extends string>({
  label,
  options,
  active,
  onPick,
}: {
  label: string;
  options: readonly T[];
  active: T;
  onPick: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1 shrink-0">
      <span className="text-xs text-muted-foreground mr-1">{label}:</span>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onPick(opt)}
          className={cn(
            'rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors',
            active === opt
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-input text-muted-foreground hover:bg-muted/40',
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function CategoryPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-2.5 py-0.5 text-[11px] font-medium border transition-colors',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'border-input text-muted-foreground hover:bg-muted/40',
      )}
    >
      {label}
    </button>
  );
}

function RuntimeBadge({ runtime }: { runtime: string }) {
  const tone =
    runtime === 'native'
      ? 'border-blue-300 text-blue-700'
      : runtime === 'ts'
        ? 'border-emerald-300 text-emerald-700'
        : runtime === 'py'
          ? 'border-amber-300 text-amber-700'
          : 'border-muted text-muted-foreground';
  return (
    <Badge variant="outline" className={cn('text-[10px] gap-1', tone)}>
      <Cpu className="h-2.5 w-2.5" />
      {runtime}
    </Badge>
  );
}

function AppliesToBadge({ appliesTo }: { appliesTo: string }) {
  const tone =
    appliesTo === 'inlet'
      ? 'border-blue-300 text-blue-700'
      : appliesTo === 'outlet'
        ? 'border-purple-300 text-purple-700'
        : appliesTo === 'both'
          ? 'border-violet-300 text-violet-700'
          : 'border-muted text-muted-foreground';
  return (
    <Badge variant="outline" className={cn('text-[10px]', tone)}>
      {appliesTo}
    </Badge>
  );
}

function CategoryIcon({ category, className }: { category: string; className?: string }) {
  switch (category) {
    case 'classification':
      return <Hash className={className} />;
    case 'pii':
      return <ShieldCheck className={className} />;
    case 'prompt-injection':
      return <ShieldCheck className={className} />;
    case 'safety':
      return <ShieldCheck className={className} />;
    case 'audit':
      return <PenSquare className={className} />;
    case 'faithfulness':
      return <ScanText className={className} />;
    case 'citation':
      return <ScanText className={className} />;
    case 'memory':
      return <Layers className={className} />;
    case 'retrieval':
      return <ScanText className={className} />;
    default:
      return <Code2 className={className} />;
  }
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className={cn('text-sm', mono && 'font-mono')}>{value}</div>
    </div>
  );
}

function runtimeToMonacoLang(runtime: string): string {
  switch (runtime) {
    case 'ts':
      return 'typescript';
    case 'py':
      return 'python';
    case 'native':
      return 'plaintext'; // native = bare-import string, not actual code
    default:
      return 'plaintext';
  }
}

// ─── New Function sheet ────────────────────────────────────────────────────
//
// Sysadmin authoring entry. Creates a draft (is_active=false,
// signature=null) — the user signs + activates from the detail dialog.

const CATEGORY_OPTIONS = [
  'classification', 'pii', 'prompt-injection', 'safety', 'memory',
  'retrieval', 'citation', 'faithfulness', 'guardrail', 'audit',
  'format', 'custom',
] as const;

const APPLIES_TO_NEW_OPTIONS = ['inlet', 'outlet', 'both'] as const;

const TS_TEMPLATE = `// TypeScript wrapper function — runs in the W3-CELL-07 sandbox.
// Receives the request context; returns a possibly-mutated context.
//
// Available helpers (see @argusai/shared/wrappers): redactPII, classify, …
//
// Latency budget enforced at the runtime; keep this fast.

export async function run(ctx) {
  // TODO: implement
  return ctx;
}
`;

const PY_TEMPLATE = `# Python wrapper function — runs in the W3-CELL-07 sandbox.
# Receives the request context; returns a possibly-mutated context.
#
# Latency budget enforced at the runtime; keep this fast.

def run(ctx):
    # TODO: implement
    return ctx
`;

function NewFunctionSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateWrapperFunction();
  const [functionKey, setFunctionKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [runtime, setRuntime] = useState<'ts' | 'py'>('ts');
  const [source, setSource] = useState(TS_TEMPLATE);
  const [category, setCategory] = useState<string>('custom');
  const [appliesTo, setAppliesTo] = useState<'inlet' | 'outlet' | 'both'>('inlet');
  const [latencyBudgetMs, setLatencyBudgetMs] = useState<number>(200);
  const [version, setVersion] = useState<string>('1.0.0');

  // When runtime changes and the source is still the previous template,
  // swap to the new template so the placeholder code matches the language.
  const handleRuntimeChange = (next: 'ts' | 'py') => {
    setRuntime(next);
    if (source === TS_TEMPLATE || source === PY_TEMPLATE) {
      setSource(next === 'ts' ? TS_TEMPLATE : PY_TEMPLATE);
    }
  };

  const reset = () => {
    setFunctionKey('');
    setDisplayName('');
    setDescription('');
    setRuntime('ts');
    setSource(TS_TEMPLATE);
    setCategory('custom');
    setAppliesTo('inlet');
    setLatencyBudgetMs(200);
    setVersion('1.0.0');
  };

  const canSubmit =
    functionKey.trim().length > 0 &&
    /^[a-z0-9_.-]+$/.test(functionKey.trim()) &&
    displayName.trim().length > 0 &&
    description.trim().length > 0 &&
    source.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await create.mutateAsync({
        functionKey: functionKey.trim(),
        displayName: displayName.trim(),
        description: description.trim(),
        runtime,
        source,
        category,
        appliesTo,
        latencyBudgetMs,
        version,
      });
      toast.success(`Created "${functionKey.trim()}" as a draft`, {
        description: 'Open the function and Sign + Activate to bring it online.',
      });
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('FUNCTION_KEY_TAKEN')) {
        toast.error('Function key taken', {
          description: 'Pick a different functionKey — keys are unique.',
        });
      } else {
        toast.error('Create failed', { description: msg });
      }
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <SheetContent side="right" className="sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New wrapper function</SheetTitle>
          <SheetDescription>
            Author a sysadmin wrapper function. New rows ship as drafts
            (<code>is_active=false</code>); from the detail dialog you save
            source, sign with the platform Ed25519 key, then activate.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="wf-key" className="text-xs">Function key (slug)</Label>
              <Input
                id="wf-key"
                value={functionKey}
                onChange={(e) => setFunctionKey(e.target.value)}
                placeholder="argus.my_filter"
                className="font-mono text-xs h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wf-runtime" className="text-xs">Runtime</Label>
              <Select value={runtime} onValueChange={(v) => handleRuntimeChange(v as 'ts' | 'py')}>
                <SelectTrigger id="wf-runtime" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ts" className="text-xs">TypeScript</SelectItem>
                  <SelectItem value="py" className="text-xs">Python</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wf-display" className="text-xs">Display name</Label>
            <Input
              id="wf-display"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="My Filter"
              className="text-sm h-8"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wf-description" className="text-xs">Description</Label>
            <Textarea
              id="wf-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this filter does and when it runs."
              rows={2}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="wf-category" className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="wf-category" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wf-applies" className="text-xs">Applies to</Label>
              <Select value={appliesTo} onValueChange={(v) => setAppliesTo(v as 'inlet' | 'outlet' | 'both')}>
                <SelectTrigger id="wf-applies" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPLIES_TO_NEW_OPTIONS.map((a) => (
                    <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wf-latency" className="text-xs">Latency budget (ms)</Label>
              <Input
                id="wf-latency"
                type="number"
                min={1}
                max={10000}
                value={latencyBudgetMs}
                onChange={(e) => setLatencyBudgetMs(Number(e.target.value) || 0)}
                className="text-xs h-8"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wf-version" className="text-xs">Version</Label>
            <Input
              id="wf-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0"
              className="font-mono text-xs h-8 max-w-32"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Source</Label>
            <div className="border rounded-md overflow-hidden h-[300px]">
              <Editor
                height="100%"
                language={runtime === 'ts' ? 'typescript' : 'python'}
                value={source}
                onChange={(v) => setSource(v ?? '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 12,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                }}
                theme="vs-dark"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Drafts ship with <code>is_active=false</code> and no signature.
              Sign + activate from the function's detail dialog.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => { reset(); onClose(); }}>
              <X className="h-3.5 w-3.5 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canSubmit || create.isPending}
              onClick={handleSubmit}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {create.isPending ? 'Creating…' : 'Create draft'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
